import { useMemo } from "react";
import PropTypes from "prop-types";
import { useController } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import ACL_RELATIONSHIPS from "acl-relationships";
import { formatError } from "helpers/formatError";
import useAuthorized from "hooks/useAuthorized";
import { swoogoKeys } from "routes/swoogo/queryKeys";
import SwoogoService from "services/Swoogo";

// Tailwind needs whole class names, so each depth maps to a literal.
const INDENT_CLASSES = [
  "tw:pl-0",
  "tw:pl-6",
  "tw:pl-12",
  "tw:pl-18",
  "tw:pl-24",
  "tw:pl-30",
];

const byName = (a, b) => a.name.localeCompare(b.name);

// Folders arrive flat. A folder whose parent is not stored is shown as a root.
const buildTree = folders => {
  const nodes = new Map(
    folders.map(folder => [folder.id, { ...folder, children: [] }])
  );
  const roots = [];
  nodes.forEach(node => {
    const parent = nodes.get(node.parent_id);
    (parent ? parent.children : roots).push(node);
  });
  nodes.forEach(node => node.children.sort(byName));
  return roots.sort(byName);
};

const collectDescendantIds = node =>
  node.children.flatMap(child => [child.id, ...collectDescendantIds(child)]);

const FolderRows = ({ nodes, depth, isCovered, selected, onToggle }) =>
  nodes.map(node => {
    const isChecked = isCovered || selected.has(node.id);
    const inputId = `swoogo-folder-${node.id}`;
    return (
      <div key={node.id}>
        <Field
          orientation="horizontal"
          className={INDENT_CLASSES[Math.min(depth, INDENT_CLASSES.length - 1)]}
        >
          <Checkbox
            id={inputId}
            checked={isChecked}
            // A folder under a selected one is shown through its parent.
            disabled={isCovered}
            onCheckedChange={checked => onToggle(node, checked)}
          />
          <FieldLabel htmlFor={inputId}>{node.name}</FieldLabel>
        </Field>
        <FolderRows
          nodes={node.children}
          depth={depth + 1}
          isCovered={isChecked}
          selected={selected}
          onToggle={onToggle}
        />
      </div>
    );
  });

FolderRows.propTypes = {
  nodes: PropTypes.array.isRequired,
  depth: PropTypes.number.isRequired,
  isCovered: PropTypes.bool.isRequired,
  selected: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

const SelectionNotice = () => (
  <Alert>
    <AlertDescription>
      No folder is selected, so Pleteo shows and syncs no Swoogo events,
      registrants, sessions or attendances.
    </AlertDescription>
  </Alert>
);

const FolderTree = ({ folders, selected, onToggle }) => {
  const tree = useMemo(() => buildTree(folders), [folders]);
  return (
    <>
      {selected.size === 0 && <SelectionNotice />}
      <div className="tw:flex tw:flex-col tw:gap-2">
        <FolderRows
          nodes={tree}
          depth={0}
          isCovered={false}
          selected={selected}
          onToggle={onToggle}
        />
      </div>
    </>
  );
};

FolderTree.propTypes = {
  folders: PropTypes.array.isRequired,
  selected: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

const FolderSkeleton = () => (
  <div className="tw:flex tw:flex-col tw:gap-2" data-testid="folders-loading">
    <Skeleton className="tw:h-5 tw:w-48" />
    <Skeleton className="tw:h-5 tw:w-40 tw:ml-6" />
    <Skeleton className="tw:h-5 tw:w-44" />
  </div>
);

const NoFolders = ({ isConfigured }) => (
  <Empty>
    <EmptyHeader>
      <EmptyTitle>No folders</EmptyTitle>
      <EmptyDescription>
        {isConfigured
          ? "No folders are stored yet. Use Refresh Folders to fetch them from Swoogo."
          : "Test the connection to list the folders from Swoogo."}
      </EmptyDescription>
    </EmptyHeader>
  </Empty>
);

NoFolders.propTypes = { isConfigured: PropTypes.bool };

const FoldersBody = ({
  isLoading,
  isConfigured,
  folders,
  selected,
  onToggle,
}) => {
  if (isLoading) {
    return <FolderSkeleton />;
  }
  if (folders.length === 0) {
    return <NoFolders isConfigured={isConfigured} />;
  }
  return (
    <FolderTree folders={folders} selected={selected} onToggle={onToggle} />
  );
};

FoldersBody.propTypes = {
  isLoading: PropTypes.bool,
  isConfigured: PropTypes.bool,
  folders: PropTypes.array.isRequired,
  selected: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Lets the admin pick which Swoogo folders Pleteo shows and syncs. Selecting a
// folder includes every folder below it, including subfolders Swoogo adds
// later, so a checked parent shows its children as checked and disabled. The
// selection is the form's `folder_ids` field, saved by Save and Sync.
const VisibleFoldersCard = ({
  isConfigured,
  isLoading,
  folders,
  onRefreshed,
}) => {
  const queryClient = useQueryClient();
  const canSync = useAuthorized([ACL_RELATIONSHIPS.swoogo.syncFolders]);
  const { field, fieldState } = useController({ name: "folder_ids" });

  const selected = useMemo(() => new Set(field.value), [field.value]);

  const refreshMutation = useMutation({
    mutationFn: SwoogoService.syncFolders,
    onSuccess: response => {
      queryClient.setQueryData(swoogoKeys.folders(), response);
      onRefreshed();
      toast.success("Folders refreshed");
    },
    onError: error =>
      toast.error(formatError(error, "The folders could not be refreshed")),
  });

  const handleToggle = (node, isChecked) => {
    const next = new Set(selected);
    if (isChecked) {
      next.add(node.id);
      // The folder now covers its descendants, so their own marks are dropped.
      collectDescendantIds(node).forEach(id => next.delete(id));
    } else {
      next.delete(node.id);
    }
    field.onChange([...next]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Visible Folders</CardTitle>
        <CardDescription>
          One Swoogo account can hold test and production events. Select the
          folders to use. A selected folder includes every folder below it,
          including subfolders added later. Events outside the selection are
          neither shown nor synced, and nothing is synced until you press Save
          and Sync.
        </CardDescription>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-4">
        <FoldersBody
          isLoading={isLoading}
          isConfigured={isConfigured}
          folders={folders}
          selected={selected}
          onToggle={handleToggle}
        />
        <FieldError>{fieldState.error?.message}</FieldError>
      </CardContent>
      {isConfigured && (
        <CardFooter className="tw:flex tw:gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={!canSync || refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
          >
            Refresh Folders
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

VisibleFoldersCard.propTypes = {
  isConfigured: PropTypes.bool,
  isLoading: PropTypes.bool,
  folders: PropTypes.array.isRequired,
  onRefreshed: PropTypes.func.isRequired,
};

export default VisibleFoldersCard;
